import type { LegalAidEvidenceListModel } from "@/api/cle-api";
import { formatLegalAidCategory } from "@/api/cle-api/models/legalAid";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import DocumentIcon from "@/components/icons/DocumentIcon";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";

import { DocumentPaginationControls } from "./DocumentPaginationControls";
import { StatusBadge } from "./StatusBadge";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "./utils/evidenceStatus";
import { formatBoolean, formatDate } from "./utils/formatters";
import type { MockPagination } from "./types";

interface LegalAidTableProps {
  legalAids: LegalAidEvidenceListModel[];
  isLoading: boolean;
  error: Error | null;
  onSelectLegalAid: (legalAidId: string) => void;
  pagination: MockPagination;
  showPagination?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export const LegalAidTable = ({
  legalAids,
  isLoading,
  error,
  onSelectLegalAid,
  pagination,
  showPagination = true,
  onPreviousPage,
  onNextPage,
}: LegalAidTableProps) => (
  <>
    <TableRoot>
      <TableHead>
        <th>Dokument</th>
        <th>Type</th>
        <th>Lektioner</th>
        <th>Uploadet den</th>
        <th>Rapporteret</th>
        <th>Status</th>
        <th>Handling</th>
      </TableHead>
      <TableBody>
        {isLoading && (
          <tr>
            <td colSpan={7} className="text-muted">
              Henter advokatvagt/retshjælp ...
            </td>
          </tr>
        )}
        {error && (
          <tr>
            <td colSpan={7}>
              <CleApiErrorText
                error={error}
                fallback="Kunne ikke hente advokatvagt/retshjælp."
              />
            </td>
          </tr>
        )}
        {!isLoading &&
          !error &&
          legalAids.map((legalAid) => (
            <tr
              key={legalAid.id}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectLegalAid(legalAid.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectLegalAid(legalAid.id);
                }
              }}
            >
              <td>
                <div className="d-flex align-items-center gap-2">
                  <DocumentIcon height={16} />
                  {legalAid.fileName ?? "-"}
                </div>
              </td>
              <td>{formatLegalAidCategory(legalAid.category)}</td>
              <td>{legalAid.numberOfLessons}</td>
              <td>{formatDate(legalAid.createdAt)}</td>
              <td>{formatBoolean(legalAid.isReported)}</td>
              <td>
                <StatusBadge
                  status={formatEvidenceStatus(legalAid.status)}
                  variant={getEvidenceStatusVariant(legalAid.status)}
                />
              </td>
              <td>
                <button
                  id={`efteruddannelse-legal-aid-${legalAid.id}`}
                  type="button"
                  className="btn btn--action"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectLegalAid(legalAid.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          ))}
        {!isLoading && !error && !legalAids.length && (
          <tr>
            <td colSpan={7} className="text-muted">
              Ingen dokumentation for advokatvagt/retshjælp
            </td>
          </tr>
        )}
      </TableBody>
    </TableRoot>
    <div className="d-flex justify-content-end mt-2">
      <DocumentPaginationControls
        pagination={pagination}
        showPagination={showPagination}
        onPreviousClick={onPreviousPage}
        onNextClick={onNextPage}
      />
    </div>
  </>
);
